import useTranslate from 'modules/local/useTranslate'
import React, {useCallback} from 'react'
import {NullAsDefault} from 'views/Shared'
import _ from 'lodash'
import {SelectCountries} from '../../../Organization/Settings/SelectCountries'
import {useAppConfig} from '../../../../modules/local/AppConfigContext'
import {FilterStatusBar} from '../../../../components/Filters/FilterStatusBar'
import LookupField from '../../../../components/LookupField'
import {Avatar} from 'antd'
import {MdChurch} from 'react-icons/md'
import {RiMapPinFill} from 'react-icons/ri'
import {Chip, SectionLabel} from './DirectoryFilterUI'

const defaultFilter = {
    keyword: undefined,
    country_id: undefined,
    diocese_id: undefined,
    org_type: undefined,
    association_type: undefined,
}

// Chưa gắn (association_id rỗng/chưa duyệt) hay đã gắn với 1 trong 3 loại - value 'none' là ước lệ
// riêng cho filter này, backend hiểu 'none' = chưa có association được duyệt (association_status=1).
const ASSOCIATION_TYPE_OPTIONS = [
    {value: 'none', label: 'not linked yet'},
    {value: 'parish', label: 'parish'},
    {value: 'diocese', label: 'diocese'},
    {value: 'religious_order', label: 'religious order'},
]

export const DirectoryOrganizationFilter = ({
                                                 value: filter = defaultFilter,
                                                 onChange: setValues = NullAsDefault,
                                             }) => {
    const t = useTranslate()
    const {countries = [], dioceses = [], org_types: org_type_options = {}} = useAppConfig('countries', 'dioceses', 'org_types')

    const handleChange = useCallback(
        (name, value) => setValues({...filter, [name]: value}),
        [filter]
    )

    const filteredParams = Object.entries(filter)
        .filter(([, value]) => value != null)
        .map(([key, value]) => {
            switch (key) {
                case 'country_id':
                    return {key, value, displayValue: _.find(countries, {id: Number(value)})?.common_name}
                case 'diocese_id':
                    return {key, value, displayValue: _.find(dioceses, {id: value})?.fullname}
                case 'org_type':
                    return {key, value, displayValue: t(org_type_options[value])}
                case 'association_type':
                    return {key, value, displayValue: t(_.find(ASSOCIATION_TYPE_OPTIONS, {value})?.label)}
                default:
                    return {key, value, displayValue: value}
            }
        })

    return (
        <div className="flex flex-col gap-3">
            <FilterStatusBar
                filteredParams={filteredParams}
                onClear={() => setValues(defaultFilter)}
                onRemove={(param) => handleChange(param, undefined)}
            />

            <div className="flex flex-col gap-1">
                <SectionLabel>{t('organization type')}</SectionLabel>
                <div className="flex flex-wrap gap-1.5">
                    {Object.keys(org_type_options).map((key) => (
                        <Chip
                            key={key}
                            active={Number(filter?.org_type) === Number(key)}
                            onClick={() => handleChange('org_type', Number(filter?.org_type) === Number(key) ? undefined : Number(key))}>
                            {t(org_type_options[key])}
                        </Chip>
                    ))}
                </div>
            </div>

            <div className="flex flex-col gap-1">
                <SectionLabel>{t('linked status')}</SectionLabel>
                <div className="flex flex-wrap gap-1.5">
                    {ASSOCIATION_TYPE_OPTIONS.map((e) => (
                        <Chip
                            key={e.value}
                            active={filter?.association_type === e.value}
                            onClick={() => handleChange('association_type', filter?.association_type === e.value ? undefined : e.value)}>
                            {t(e.label)}
                        </Chip>
                    ))}
                </div>
            </div>

            <div className="flex flex-col gap-1">
                <SectionLabel>{t('country')}</SectionLabel>
                <SelectCountries
                    value={filter?.country_id || undefined}
                    allowClear={true}
                    onChange={(value) => handleChange('country_id', Number(value) || undefined)}
                    placeholder={t('click to select')}
                />
            </div>

            <div className="flex flex-col gap-1">
                <SectionLabel>{t('diocese')}</SectionLabel>
                <LookupField
                    title={t('choose diocese')}
                    placeholder={t('click to select')}
                    createIfNotExists={false}
                    defaultValue={_.find(filteredParams, {key: 'diocese_id'})?.displayValue}
                    apiPath="/lookup/diocese/get-by-types"
                    OptionWrapper={({children, ...props}) =>
                        <div className="flex items-center gap-2 border border-color-50 hover:shadow-md rounded-lg relative" {...props}>
                            {children}
                        </div>
                    }
                    renderOption={(o, i) => (
                        <div key={i} className="flex items-center flex-1 gap-3">
                            <Avatar
                                size={36} src={o.avatar}
                                className="flex flex-center background-200 rounded-l-lg rounded-r-0"
                                icon={<MdChurch size={16} style={{color: '#dddddd'}}/>}
                            />
                            <div className="py-1 flex flex-col flex-1 gap-0.5">
                                <span className="font-bold text-sm text-color-000">{o.fullname}</span>
                                {o.location_address && (
                                    <div className="flex items-center gap-1">
                                        <RiMapPinFill size={9} className="text-color-400"/>
                                        <span className="max-lines-1 text-xs text-color-400 italic">{o.location_address}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    handleChange={(e) => handleChange('diocese_id', e.target.id)}
                />
            </div>
        </div>
    )
}

export default DirectoryOrganizationFilter
